import { Delete, EditGlyph, OverflowMenu } from '@wfp/icons-react';
import { Text } from '@wfp/react';
import React, { useEffect, useRef, useState } from 'react';

import type { IComment, ICommentPermissions } from 'src/types';

interface Props {
  permissions: ICommentPermissions;
  closed?: boolean;
  user: { username?: string };
  activeUser?: string;
  data: { pk?: string | number; comments?: IComment[] };
  toggleDeleteConfirmationModal: () => void;
  toggleEdit: () => void;
  isReportPublished?: boolean;
  nested?: boolean;
  parentClosed?: boolean;
  isCommentsUpdating?: boolean;
}

const hasAnotherUserReplies = (
  comments: IComment[] | undefined,
  activeUser?: string,
) => !!comments?.some(item => item.user.username !== activeUser);

const useClickOutside = (
  ref: React.RefObject<HTMLElement>,
  onOutside: () => void,
) => {
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onOutside();
      }
    };
    window.addEventListener('click', handleClickOutside);
    window.addEventListener('touchstart', handleClickOutside);
    return () => {
      window.removeEventListener('click', handleClickOutside);
      window.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);
};

function getIconsVisibility({
  permissions,
  nested,
  closed,
  parentClosed,
  username,
  activeUser,
  anotherUserReplied,
}: {
  permissions: ICommentPermissions;
  nested: boolean;
  closed?: boolean;
  parentClosed: boolean;
  username?: string;
  activeUser?: string;
  anotherUserReplied: boolean;
}) {
  const {
    delete_issue,
    delete_own_issue,
    change_issue,
    change_own_issue,
    change_comment,
    change_own_comment,
    delete_own_comment,
  } = permissions;

  if (!nested) {
    return {
      displayEditIcon:
        !closed &&
        ((!!change_own_issue && username === activeUser) || !!change_issue),
      displayDeleteIcon:
        !closed &&
        ((!!delete_own_issue && username === activeUser) || !!delete_issue) &&
        (!anotherUserReplied || !!delete_issue),
    };
  }

  return {
    displayEditIcon:
      !parentClosed &&
      ((!!change_own_comment && username === activeUser) || !!change_comment),
    displayDeleteIcon:
      !parentClosed && !!delete_own_comment && username === activeUser,
  };
}

const ReviewIcon = ({
  permissions,
  closed,
  user,
  activeUser,
  data,
  toggleDeleteConfirmationModal,
  toggleEdit,
  isReportPublished,
  nested = false,
  parentClosed = false,
  isCommentsUpdating,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useClickOutside(containerRef, () => setIsOpen(false));

  const { displayEditIcon, displayDeleteIcon } = getIconsVisibility({
    permissions,
    nested,
    closed,
    parentClosed,
    username: user.username,
    activeUser,
    anotherUserReplied: hasAnotherUserReplies(data?.comments, activeUser),
  });

  const editIcon = displayEditIcon && (
    <EditGlyph color="#71767E" className="noselect" />
  );
  const deleteIcon = displayDeleteIcon && (
    <Delete fill="#71767E" id={data.pk} className="noselect" />
  );

  if (!displayDeleteIcon && !displayEditIcon) return null;

  return (
    <div
      ref={containerRef}
      className={`dropdown${isOpen ? ' dropdown--active' : ''}`}
    >
      <button
        type="button"
        className="dropdown__trigger"
        style={{ border: 'none', background: 'none', padding: 0 }}
        onClick={e => {
          e.stopPropagation();
          setIsOpen(prev => !prev);
        }}
      >
        <OverflowMenu
          width="20"
          height="20"
          className="acr-comment-dropdown-menu"
          color="#595F65"
        />
      </button>
      {isOpen && (
        <div className="dropdown__content">
          <div>
            {displayEditIcon && !closed && (
              <div
                onClick={() => {
                  if (!isCommentsUpdating) {
                    setIsOpen(false);
                    toggleEdit();
                  }
                }}
                className="acr-comments-action"
              >
                <span>{editIcon}</span>
                <Text>Edit</Text>
              </div>
            )}
            {displayDeleteIcon && !isReportPublished && (
              <div
                onClick={toggleDeleteConfirmationModal}
                className="acr-comments-action"
              >
                <span>{deleteIcon}</span>
                <Text>Delete</Text>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewIcon;
